import axios, { Axios, AxiosInstance } from 'axios'
import { debugLog } from '../utils/log-helper'
import {
  checkAuthToken,
  getAccessToken,
  refreshAccessToken,
  triggerAuthFailure,
} from './auth-helper'

// Create an axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVICE_URL, // Set your base URL here
  timeout: 300000, // Set a reasonable timeout 5 minutes
  headers: {
    'Content-Type': 'application/json',
  },
})

// Single-flight access-token refresh. When a token expires, many in-flight requests can all need a
// fresh one at once; sharing one refresh promise avoids stampeding Auth0 and guarantees every
// caller retries with the same new token. Reset once settled so the next expiry can refresh again.
let refreshPromise: Promise<string | undefined> | null = null
const refreshTokenOnce = async (): Promise<string | undefined> => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken(true).finally(() => {
      refreshPromise = null
    })
  }
  return await refreshPromise
}

// Add a request interceptor to include the auth token if available. If the stored token is present
// but already expired, refresh it proactively so the request never has to fail with a 401 first.
axiosInstance.interceptors.request.use(
  async (config) => {
    let accessToken = getAccessToken()
    if (accessToken && !checkAuthToken()) {
      const fresh = await refreshTokenOnce()
      if (fresh) {
        accessToken = fresh
      }
    }
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  async (error) => {
    // Handle request error
    return await Promise.reject(error)
  },
)

// Add a response interceptor to handle responses globally
axiosInstance.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    // On a 401 (expired/rejected token), transparently refresh the access token once and retry the
    // original request, so an expired session self-heals instead of forcing an app restart.
    // `_retry` prevents an infinite loop if the refreshed token is also rejected.
    const original = error.config
    if (
      error.response &&
      error.response.status === 401 &&
      original &&
      !original._retry
    ) {
      original._retry = true
      // Only a request that carried a token belongs to an authenticated session — if the refresh
      // fails for one of these the refresh token itself is dead and we must re-login. A truly
      // anonymous/guest request that 401s must NOT be hijacked into a login wall, so gate on this.
      const hadToken = getAccessToken() !== ''
      const token = await refreshTokenOnce()
      if (token) {
        original.headers = original.headers ?? {}
        original.headers.Authorization = `Bearer ${token}`
        return await axiosInstance(original)
      }
      if (hadToken) {
        // Refresh token is dead — kick off an automatic re-login (debounced to fire once even when
        // many requests 401 together), then still reject so this in-flight call settles.
        triggerAuthFailure()
      }
    }
    return await Promise.reject(error)
  },
)

export const okaoAxiosNoAuth = (): Axios => {
  debugLog('okaoAxiosNoAuth: ', axiosInstance.defaults)
  if (axiosInstance.defaults.headers.common.Authorization) {
    delete axiosInstance.defaults.headers.common.Authorization
  }
  return axiosInstance
}

export default axiosInstance
